import * as i0 from '@angular/core';
import { inject, DOCUMENT, Injectable } from '@angular/core';
import { EnvironmentService } from '@tc/foundation';
import { HttpClient } from '@angular/common/http';
import { first } from 'rxjs/operators';

var Severity;
(function (Severity) {
    Severity[Severity["Trace"] = 1] = "Trace";
    Severity[Severity["Debug"] = 2] = "Debug";
    Severity[Severity["Information"] = 3] = "Information";
    Severity[Severity["Warning"] = 4] = "Warning";
    Severity[Severity["Error"] = 5] = "Error";
    Severity[Severity["Critical"] = 6] = "Critical";
})(Severity || (Severity = {}));

class LogEntry {
    appName;
    source;
    severity;
    data;
    tags;
    userAgent;
    constructor(application, source, severity, data, tags, userAgent) {
        this.appName = application;
        this.source = source;
        this.severity = severity;
        this.data = data;
        this.tags = tags;
        this.userAgent = userAgent;
    }
}

class LoggerService {
    envService = inject(EnvironmentService);
    http = inject(HttpClient);
    document = inject(DOCUMENT);
    hasWarnedOfNoLogging = false;
    log(source, severity, data, tags) {
        const userAgent = this.document?.defaultView?.navigator?.userAgent;
        this.envService.configuration$
            .pipe(first())
            .subscribe((configuration) => {
            const logEntry = new LogEntry(configuration.applicationName, source, severity, data, tags, userAgent);
            if (configuration.isApiLoggingEnabled &&
                severity >= configuration.logLevel) {
                this.writeToApi(logEntry, configuration);
            }
            if (configuration.isConsoleLoggingEnabled &&
                severity >= configuration.logLevel) {
                this.writeToConsole(logEntry);
            }
            if (!this.hasWarnedOfNoLogging &&
                configuration.logEndpoint == null &&
                !configuration.isConsoleLoggingEnabled) {
                console.warn('********** Logging Disabled **********');
                this.hasWarnedOfNoLogging = true;
            }
        });
    }
    writeToApi(logEntry, configuration) {
        if (logEntry.source.endsWith(configuration.logEndpoint)) {
            return;
        }
        const additionalData = [];
        if (logEntry.data?.username)
            additionalData.push(`User: ${logEntry.data.username}`);
        if (logEntry.data?.name)
            additionalData.push(`Name: ${logEntry.data.name}`);
        if (logEntry.data?.datetime)
            additionalData.push(`DateTime: ${logEntry.data.datetime}`);
        if (logEntry.data?.size)
            additionalData.push(`Size: ${logEntry.data.size}`);
        if (logEntry.data?.speed)
            additionalData.push(`Speed: ${logEntry.data.speed}`);
        if (logEntry.data?.success != null)
            additionalData.push(`Success: ${logEntry.data.success}`);
        if (logEntry.data?.url)
            additionalData.push(`URL: ${logEntry.data.url}`);
        if (logEntry.data?.headers)
            additionalData.push(`Headers: ${JSON.stringify(logEntry.data.headers)}`);
        if (logEntry.data?.error)
            additionalData.push(`Error: ${JSON.stringify(logEntry.data.error)}`);
        if (logEntry.data?.status)
            additionalData.push(`Status: ${logEntry.data.status}`);
        if (logEntry.data?.statusText)
            additionalData.push(`Status Text: ${logEntry.data.statusText}`);
        const apiLogEntry = {
            appName: logEntry.appName,
            source: logEntry.source,
            logLevel: Severity[logEntry.severity],
            version: logEntry.userAgent,
            message: logEntry.data?.message,
            tags: ['UI', 'WebApp', ...(logEntry.tags ?? [])],
            exception: logEntry.data?.stack
                ? {
                    message: logEntry.data.message,
                    source: logEntry.source,
                    stackTrace: logEntry.data.stack,
                }
                : null,
            additionalData: [...additionalData],
        };
        const apiUrl = configuration.logApiUrl
            ? configuration.logApiUrl
            : configuration.apiUrl;
        const url = `${apiUrl}/${configuration.logEndpoint}`;
        this.http.post(url, apiLogEntry).subscribe();
    }
    writeToConsole(logEntry) {
        const message = logEntry.data?.message ?? logEntry.source;
        const additionalData = logEntry.data?.error ?? logEntry.data?.stack;
        switch (logEntry.severity) {
            case Severity.Critical:
            case Severity.Error:
                if (additionalData) {
                    console.error(message, additionalData);
                }
                else {
                    console.error(message);
                }
                break;
            case Severity.Warning:
                if (additionalData) {
                    console.warn(message, additionalData);
                }
                else {
                    console.warn(message);
                }
                break;
            default:
                if (additionalData) {
                    console.log(message, additionalData);
                }
                else {
                    console.log(message);
                }
                break;
        }
    }
    static ɵfac = function LoggerService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || LoggerService)(); };
    static ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: LoggerService, factory: LoggerService.ɵfac, providedIn: 'root' });
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LoggerService, [{
        type: Injectable,
        args: [{ providedIn: 'root' }]
    }], null, null); })();

class MockLoggerService {
    log(source, severity, message, stack, tags) { }
}

/*
 * Public API Surface of logger
 */

/**
 * Generated bundle index. Do not edit.
 */

export { LogEntry, LoggerService, MockLoggerService, Severity };

